import React, { Children, isValidElement } from 'react';
import { useRouter } from 'next/router';

import { NodeProps } from './shared';
import { Icon } from '@greenlightme/ui/dist/v2';
import cx from 'classnames';

function extractTextFromMixedContent(children: React.ReactNode): string {
  if (typeof children === 'string') {
    return children;
  }

  if (Array.isArray(children)) {
    return children
      .map((child) => {
        if (typeof child === 'string') {
          return child;
        }
        if (isValidElement(child) && (child.props as any).children) {
          return extractTextFromMixedContent((child.props as any).children);
        }
        return '';
      })
      .join('');
  }

  return '';
}

function processListContent(children: React.ReactNode): React.ReactNode {
  if (typeof children === 'string') {
    return children.replace(/^\s*[-–—]\s*/, '');
  }

  if (Array.isArray(children)) {
    const firstElement = children[0];
    if (typeof firstElement === 'string' && firstElement.match(/^\s*[-–—]/)) {
      const processed = [...children];
      processed[0] = firstElement.replace(/^\s*[-–—]\s*/, '');
      return processed;
    }
    return children;
  }

  return children;
}

export function BlockquoteNode({ children }: NodeProps) {
  const router = useRouter();

  const isLearningCenter = router.pathname.includes('learning-center');

  if (isLearningCenter) {
    return (
      <div
        className={cx(
          'mb-8 w-full border-y border-solid border-[#dfe2e2] py-6 text-left'
        )}
      >
        {Children.map(children, (child) => {
          if (!isValidElement(child)) return child;

          const element = child as React.ReactElement;
          const childContent = element.props.children;
          const text = extractTextFromMixedContent(childContent);

          const isTitle =
            element.props?.node?.content?.[0]?.marks?.[0]?.type === 'bold';
          const isList = text.match(/^\s*[-–—]/);

          if (isTitle) {
            return (
              <div className="[&>p]:!typography-display-4 mb-6">{child}</div>
            );
          }

          if (isList) {
            return (
              <div className="mb-2 flex items-start last:mb-0">
                <Icon
                  fileName="ic_check_circle_filled"
                  size="md"
                  className="mr-4 mt-1 !h-4 !w-4 flex-shrink-0 text-teal-400"
                  data-testid="checkmark"
                />
                <div className="flex-1 text-lg font-[300]">
                  {processListContent(childContent)}
                </div>
              </div>
            );
          }

          return child;
        })}
      </div>
    );
  }

  return (
    <div className="-mx-[8.333%] my-6 rounded-[3.5rem] bg-spearmint px-[8.333%] py-8 text-center tablet:my-8 tablet:rounded-[4rem] tablet:px-[8.333%] tablet:py-12 tablet:text-left">
      {children}
    </div>
  );
}
